import { useEffect, useState } from 'react';
import { SettingsValueProps } from 'src/components/settings/type';
import { defaultSettings } from 'src/config';
import { Coding } from 'src/careflow/ph-core/fhir-client/resources';
import useRegistry from 'src/careflow/ph-core/multi-tenant/registry/useRegistry';
import { ComponentsConfig } from './useComponentsConfigData';
import { getTenantCodeFromCareflowToken } from 'src/careflow/ph-core/authentication/util/token';

export enum BannerInfoName {
  MRN = 'patientMRN',
  AGE = 'patientAge',
  BIRTHDAY_AGE = 'patientBirthDateAge',
  AGE_GROUP = 'patientAgeGroup',
  GENDER = 'patientGender',
  PRIMARY_PHONE = 'patientPrimaryPhone',
  PRIMARY_EMAIL = 'patientPrimaryEmail',
  MEDICAID = 'patientMedicaId',
  PRIMARY_ZIPCODE = 'patientPrimaryZipCode',
  MANAGING_ORG = 'patientManagingOrg',
  SIL_SCORE = 'patientSILScore',
  EOC = 'episodeOfCare',
}

export interface BannerData {
  order: number;
  name: BannerInfoName;
}

export interface TenantThemeConfig {
  logo: string;
  tenantName: string;
  pagePrefix: string;
  tenantFavicon: string;
  tenantSpinnerLogo: string;
  url?: string;
  settings: SettingsValueProps;
  disclaimerText?: string;
  environment?: string;
  isToClose?: string;
  bgColor?: string;
}

export interface CareTeamTenantConfigData {
  defaultValues?: {
    category?: Coding;
    status?: string;
  };
  readOnly?: string[];
}

export interface MyDashboardPatientsDashboardData {
  filters?: {
    generalPractitioner: boolean;
    careTeamMember: boolean;
  };
}

export interface MyDashboardConfigData {
  patientsDashboard?: MyDashboardPatientsDashboardData;
}

export interface WorkflowDisposition {
  showProviderWindow?: boolean;
}

export interface HelpLinkPiece {
  text?: string;
  url?: string;
  properties?: Record<string, any>;
}

export interface HelpLinkItem {
  orgNames?: string[]; // empty or absent = show to all users (fallback)
  pieces: HelpLinkPiece[];
}

interface ExternalLinkData {
  buttonTitle?: string;
  url?: string;
  fallbackUrl?: string;
  substitutions?: Record<string, string>;
}

interface SearchMemberOption {
  label: string;
  value: string;
}
export interface SearchMemberOptions {
  taskSelectRequester?: SearchMemberOption[];
  taskSelectOwner?: SearchMemberOption[];
  taskSelectDefault?: SearchMemberOption[];
  referralReferredFromPDInternal?: SearchMemberOption[];
  referralReferredFrom?: SearchMemberOption[];
  referralPerformerToItem?: SearchMemberOption[];
  consentGrantee?: SearchMemberOption[];
  careTeamMemberType?: SearchMemberOption[];
  customFieldReference?: SearchMemberOption[];
  episodeOfCareCareManager?: SearchMemberOption[];
}

export interface TenantConfigData {
  finishedLoading?: boolean;
  languageResource?: string | null;
  theme?: TenantThemeConfig;
  componentsData: {
    carePlan: any;
    menu: any;
    header: {
      helpLinks?: HelpLinkItem[];
      [key: string]: any;
    };
    workflow: any;
    showSILInformation?: boolean;
    showMedicaID?: boolean;
    showOptInStatus?: boolean;
    showGender?: boolean;
    case?: {
      attachments?: {
        byCase: boolean;
      };
      outboundReferrals?: {
        categoryFilter?: string[];
      };
    };
    showSubGoals?: {
      includeSubGoals: boolean;
      noOfSubGrids: number;
    };
    episodeOfCare?: {
      defaultValues?: {
        type?: Coding;
      };
      onlyPatientDocuments?: boolean;
    };
    careTeam?: CareTeamTenantConfigData;
    practitioner?: {
      providerDirectoryURL?: string;
      address?: any;
      list?: any;
      requiredEmail?: boolean;
      requiredPhone?: boolean;
      requiredAddress?: boolean;
    };
    workflowDisposition?: WorkflowDisposition;
    myDashboard?: MyDashboardConfigData;
    observation?: {
      defaultValues?: {
        category?: {
          code?: string;
        };
      };
    };
    consent?: {
      defaultValues?: {
        status?: string;
        scope?: string;
      };
    };
    patientLandingPageRoute?: {
      [key: string]: string;
    };
    userSupport?: {
      urlForm?: string;
    };
    externalLink?: {
      [key: string]: ExternalLinkData;
    };
    user?: {
      requiredPhone?: boolean;
    };
    searchMember?: {
      searchMemberOptions?: SearchMemberOptions;
      selectCareTeam?: boolean;
      selectMember?: boolean;
    };
    patient?: {
      address?: {
        administrativeFields?: Array<{
          name: string;
          visible?: boolean;
          label?: string;
          googleComponent?: string;
          fhirField?: string;
          saveToFhir?: boolean;
          extensionUrl?: string;
        }>;
        requiredHousingStatus?: boolean;
        defaultHousingStatus?: string;
      };
      list?: {
        identifiers?: [
          {
            icon?: string;
            system?: string;
            code?: string;
            properties?: any;
          }
        ];
      };
    };
    relatedPerson?: {
      address?: any;
    };
    referral?: {
      addServiceManually?: boolean;
      addPerformerManually?: boolean;
      serviceRequired?: boolean;
      performerRequired?: boolean;
      performerCategory?: { value: string; label: string }[];
      addresses?: { observationFilter: string[]; conditionFilter: [] };
      autoCloseTaskOnReferralCreation?: boolean;
      showReferralInsideTask?: boolean;
      showServicesCheckbox?: boolean;
    };
    task?: {
      filters?: {
        status?: [
          {
            code: string;
            display: string;
          }
        ];
      };
      planDefinition?: {
        [key: string]: string;
      };
      statusCountByPathway?: {
        open: string[];
        close: string[];
      };
      isIncludePatientTask?: string;
      taskTypeMappings?: {
        [taskCode: string]: {
          displayName?: string;
          translationKey?: string;
          actionType: string;
          enabled: boolean;
          permission?: string | null;
        };
      };
      activityDefintionsDisabledStatus?: string[];
      inputTypeFiles?: string[];
      outputTypeFiles?: string[];
      taskInputTypesExtensionUrl?: string;
    };
    goal?: {
      statusCountByPathway?: {
        open: string[];
        close: string[];
      };
      tasksSuggestions?: {
        allSelected: boolean;
      };
    };
    hjInvitation?: {
      showScores?: boolean;
    };
    organization?: {
      list?: {
        identifiers?: [
          {
            icon?: string;
            system?: string;
            code?: string;
            properties?: any;
          }
        ];
      };
      rules?: any;
    };
    customFields?: {
      allUserOrganizations?: boolean;
      filterVSByResource?: boolean;
    };
  };
  componentsConfigData?: ComponentsConfig;
  configurations?: any;
}

export interface TenantConfigDataResponse {
  configData: TenantConfigData;
}

export const initialTenantConfigData: TenantConfigData = {
  finishedLoading: false,
  languageResource: null,
  theme: {
    tenantName: 'MyCareCircle',
    logo: '/logo/my_care_circle_horizontal_logo.png',
    pagePrefix: 'MyCareCircle - ',
    tenantFavicon: '/favicon/MyCareCircle-Icon.png',
    tenantSpinnerLogo: '/logo/my_care_circle_horizontal_logo.png',
    settings: defaultSettings,
    disclaimerText: undefined,
    environment: undefined,
    isToClose: undefined,
    bgColor: undefined,
  },
  componentsData: {
    carePlan: {},
    menu: {},
    header: {},
    workflow: {},
    showSILInformation: false,
    showMedicaID: false,
    showOptInStatus: false,
    showGender: true,
    patient: {
      address: {
        administrativeFields: [
          {
            name: 'county',
            visible: true,
            label: 'County',
            googleComponent: 'administrative_area_level_2',
            fhirField: 'district',
            saveToFhir: true,
          },
        ],
      },
    },
  },
};

const useTenantConfigData = (): TenantConfigData => {
  const [tenantConfigData, setTenantConfigData] =
    useState<TenantConfigData>(initialTenantConfigData);
  const { data: registry } = useRegistry({ filter: { keyRegistry: 'tenant-config-data' } });

  useEffect(() => {
    const userTenantCode = getTenantCodeFromCareflowToken();
    if (!registry?.[0] || !userTenantCode) return;
    try {
      const parsedData = JSON.parse(registry[0].keyValue);

      let tenantConfig;
      if (parsedData.configData) {
        tenantConfig = parsedData as TenantConfigDataResponse[];
      } else if (parsedData.theme || parsedData.componentsData) {
        tenantConfig = [{ configData: parsedData }] as TenantConfigDataResponse[];
      }

      if (tenantConfig) {
        const { configData } = tenantConfig[0];
        setTenantConfigData({
          ...initialTenantConfigData,
          ...configData,
          theme: {
            ...initialTenantConfigData.theme,
            ...configData.theme,
          },
          finishedLoading: true,
        });
      }
    } catch (error) {}
  }, [registry]);

  return tenantConfigData;
};

export default useTenantConfigData;
